/**
 * Canonical host + URL builder for customer-facing VirtualSpace
 * share pages.
 *
 * Pure constant module — no env vars, no server imports. Safe to import
 * from client components without dragging `site-url.ts`'s
 * `process.env.*` reads into the browser bundle.
 *
 * URL pattern (2026-05-23, tier-prefixed canonical):
 *   - `explore.virtualspace.xyz/launch/<token>` — Launch tier (atomic embed)
 *   - `explore.virtualspace.xyz/orbit/<token>`  — Orbit tier (paid mid-tier)
 *   - `explore.virtualspace.xyz/landing/<token>` — Landing tier (paid premium)
 *
 * Legacy `explore.virtualspace.xyz/explore/<token>` 308-redirects to the
 * canonical tier path; old customer links / OG cards continue to work
 * but everything re-canonicalizes over time.
 *
 * Why a constant host instead of an env var: the host is brand-stable +
 * the same in dev / preview / prod. Different from `getSiteUrl()`
 * which VARIES across environments (localhost, vercel preview URL,
 * prod). A shared `explore.virtualspace.xyz` constant means a "Copy
 * share URL" button clicked from any operator dashboard environment
 * always emits the production customer-facing URL — which is what
 * every operator wants to paste into a customer email regardless of
 * where they're working from.
 *
 * Multi-domain split rationale (2026-05-21):
 *   - spatialist.xyz hosts marketing on the apex.
 *   - app.spatialist.xyz hosts the operator + customer dashboard +
 *     APIs (the canonical login surface).
 *   - explore.virtualspace.xyz hosts customer-facing VirtualSpace
 *     pages (Launches / Orbits / Landings), public share surfaces
 *     (`/share/<token>`, `/share/order|proposal|hosting/<token>`),
 *     tokenized SpaceManager / order-portal hubs
 *     (`/space-manager/<token>`, `/order-portal/<token>`), and the
 *     public Get Started intake (`/get-started`, AD #226), the
 *     Custom Fit explorer (`/services`, AD #390), and Global Coverage
 *     (`/coverage`).
 *     Point clouds joined 2026-07-20; remaining public shares +
 *     hubs followed 2026-07-22 (founder: retire app.spatialist.xyz
 *     for customer-facing share/hub links).
 *   - spatialist.com + app.spatialist.com 301-redirect to their
 *     spatialist.xyz counterparts on web hits (only role is as the
 *     outbound email/SMS sender domain for better deliverability).
 *
 * See `getCanonicalShareUrl` in `site-url.ts` for the entity-type-
 * aware variant that routes public share tokens here. Auth-gated
 * upload / tech stay on portal or operator hosts; authenticated
 * SpaceManager lives on `my.virtualspace.xyz` (`/portal/vs/*`).
 */
export const VIRTUAL_SPACE_HOST = "https://explore.virtualspace.xyz";

/**
 * Flat `/share/<token>` entity types that mint + redirect on
 * {@link VIRTUAL_SPACE_HOST} (no tier prefix). Path-prefixed public
 * shares (order / proposal / hosting) use the same host via
 * {@link buildExplorePublicShareUrl}.
 */
export const EXPLORE_FLAT_SHARE_ENTITY_TYPES = new Set([
  "point_cloud_set",
  "program",
  "program_hub",
]);

/** Absolute public share URL on the VirtualSpace explore host. */
export function buildExplorePublicShareUrl(path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${VIRTUAL_SPACE_HOST}${clean}`;
}

/**
 * Canonical customer-facing Get Started URL
 * (`https://explore.virtualspace.xyz/get-started`).
 *
 * Always emit this absolute URL when pasting into customer email,
 * RCS review packets, Webflow CTAs, or sales collateral — never
 * `ops.` / `spatialist.xyz` / apex marketing hosts. Operator-host
 * hits 308 to this canonical (AD #226).
 *
 * Optional `search` preserves query params (`?embed=1`, `?fresh=1`).
 */
export function buildGetStartedUrl(search?: string | null): string {
  const base = `${VIRTUAL_SPACE_HOST}/get-started`;
  if (!search) return base;
  const prefix = search.startsWith("?") ? search : `?${search}`;
  return `${base}${prefix}`;
}

/**
 * Canonical customer-facing Custom Fit URL
 * (`https://explore.virtualspace.xyz/services`).
 *
 * Operator-host hits 308 here (same posture as Get Started). Webflow
 * can iframe `?embed=1` later; do not treat this as the Webflow
 * marketing listing on virtualspace.xyz.
 */
export function buildServicesUrl(search?: string | null): string {
  const base = `${VIRTUAL_SPACE_HOST}/services`;
  if (!search) return base;
  const prefix = search.startsWith("?") ? search : `?${search}`;
  return `${base}${prefix}`;
}

/**
 * Canonical customer-facing Global Coverage URL
 * (`https://explore.virtualspace.xyz/coverage`).
 *
 * Operator-host hits 308 here (same posture as Get Started). Webflow
 * marketing `/coverage` iframes `?embed=1`; do not treat this as the
 * apex `virtualspace.xyz` listing.
 */
export function buildCoverageUrl(search?: string | null): string {
  const base = `${VIRTUAL_SPACE_HOST}/coverage`;
  if (!search) return base;
  const prefix = search.startsWith("?") ? search : `?${search}`;
  return `${base}${prefix}`;
}

/**
 * VirtualSpace tier enum (mirrors `virtualspace.virtual_space_tier` in
 * Postgres). Kept as a constant map so the type + the path slug stay
 * in lockstep: changing one rejects the build.
 */
export const VIRTUAL_SPACE_TIER_PATHS = {
  launch: "launch",
  orbit: "orbit",
  landing: "landing",
} as const;

export type VirtualSpaceTier = keyof typeof VIRTUAL_SPACE_TIER_PATHS;

/**
 * All 3 tier path slugs as a readonly array — useful for route
 * matching + redirect-loop guards.
 */
export const VIRTUAL_SPACE_TIER_PATH_SLUGS: ReadonlyArray<
  (typeof VIRTUAL_SPACE_TIER_PATHS)[VirtualSpaceTier]
> = ["launch", "orbit", "landing"];

/**
 * Type guard for unknown strings → VirtualSpaceTier.
 */
export function isVirtualSpaceTier(value: unknown): value is VirtualSpaceTier {
  return (
    typeof value === "string" &&
    (value === "launch" || value === "orbit" || value === "landing")
  );
}

/**
 * Build the canonical customer-facing URL for a VirtualSpace token.
 *
 * Always emits the production `explore.virtualspace.xyz/<tier>/<token>`
 * URL regardless of where this is called from (operator dashboard,
 * server action, email worker). Same output in dev, preview, and
 * production so the URL pasted into a customer email is always the
 * one a customer can actually visit.
 *
 * `tier` is required — every caller has the VS row in scope, so
 * reading `vs.tier` to pass here is free. Missing tier would silently
 * default to 'launch' which would 308-redirect on the customer's
 * first hit (still functional but adds a redirect hop + a noisy
 * canonical-URL mismatch for OG scrapers); requiring it surfaces the
 * mistake at compile time.
 */
export function buildVirtualSpaceShareUrl(
  token: string,
  tier: VirtualSpaceTier,
): string {
  return `${VIRTUAL_SPACE_HOST}/${VIRTUAL_SPACE_TIER_PATHS[tier]}/${token}`;
}

/**
 * Legacy `/explore/<token>` URL. ONLY use when minting a fallback
 * link that we want to land on the legacy redirect path (rare —
 * almost always you want the tier-prefixed canonical instead). The
 * legacy route 308-redirects to the canonical, so this still works
 * for customers but adds a redirect hop.
 *
 * Kept as a separate function so call sites that genuinely need the
 * legacy URL (e.g. a back-compat OG canonical) don't accidentally
 * drop the tier from the canonical builder.
 */
export function buildLegacyVirtualSpaceShareUrl(token: string): string {
  return `${VIRTUAL_SPACE_HOST}/explore/${token}`;
}
